import { Box, Grid, SwipeableDrawer, Typography, useMediaQuery } from '@mui/material';
import { useTranslation } from '@/hooks/translations';
import { Section } from '@/components/common';
import { getCurrencyWithAmount } from '@/common/utility';
import { ICardData } from '../types';
import DashboardCard from '../DashboardCard';

const DiscountDetailsDrawer = ({
    open,
    onClose,
    discountValues,
    currencySymbol,
    currencyCode,
}: {
    open: boolean;
    onClose: () => void;
    discountValues: ICardData[];
    currencySymbol: string;
    currencyCode: string;
}) => {
    const { t } = useTranslation();

    const downSm = useMediaQuery('(max-width: 600px)');
    const headerFontSize = downSm ? 16 : 20;
    const valueFontSize = downSm ? 20 : 24;

    const discountTitleTranslationSwitch = (title: string) => {
        switch (title) {
            case 'loyaltyGeneralDiscount':
                return t('Qlub + Discount');
            case 'discounts':
                return t('Discounts');
            default:
                return title;
        }
    };

    const totalCountOfDiscounts = discountValues.reduce((acc, discountValue) => {
        if (
            discountValue.title === 'loyaltyGeneralDiscountCount' ||
            discountValue.title === 'loyaltyWelcomeDiscountCount'
        ) {
            return acc + (Number(discountValue.value) || 0);
        }
        return acc;
    }, 0);

    const loyaltyGeneralDiscountCount =
        discountValues.find((discountValue) => discountValue.title === 'loyaltyGeneralDiscountCount')?.value || 0;

    const discountValuesTotal = discountValues?.reduce((acc, card) => {
        if (card.title === 'loyaltyGeneralDiscount' || card.title === 'discounts') {
            return acc + (Number(card.value) || 0);
        }
        return acc;
    }, 0);

    const discountTitleSize = downSm ? 16 : 20;

    const discountDetailRowComponent = (title: string, value: string, transactionCount: string | number) => {
        return (
            <Section
                sx={{
                    border: '1px solid #E5E5E5',
                    backgroundColor: '#FAFAFA',
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    borderRadius: '0px',
                    cursor: 'default',
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                    <Typography variant="h5" fontWeight={'bold'} sx={{ fontSize: discountTitleSize }}>
                        {discountTitleTranslationSwitch(title)}
                    </Typography>
                    <Typography variant="h6" sx={{ fontSize: discountTitleSize }}>
                        {transactionCount} {t('transactions')}
                    </Typography>
                </Box>
                <Typography variant="h6" color={'primary.main'} sx={{ fontSize: discountTitleSize }}>
                    {getCurrencyWithAmount(value, currencySymbol, currencyCode)}
                </Typography>
            </Section>
        );
    };

    return (
        <SwipeableDrawer
            open={open}
            onClose={onClose}
            anchor="bottom"
            onOpen={() => {
                // do nothing
            }}
            PaperProps={{
                sx: {
                    margin: 'auto',
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    overflow: 'hidden',
                    justifyContent: 'space-between',
                },
            }}
        >
            <div style={{ padding: '1rem', overflowY: 'auto', display: 'flex', justifyContent: 'center' }}>
                <Grid item xs={12} md={10} lg={8}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="h5" fontWeight={'bold'}>
                            {t('Discount Breakdown')}
                        </Typography>
                        <Typography variant="h6">{t('Detailed analysis of all discount transactions')}</Typography>
                    </Box>
                    <Grid container spacing={2} sx={{ marginTop: 2 }}>
                        <Grid item xs={12} md={6}>
                            <DashboardCard
                                headerText={t('Total Discount Transactions')}
                                value={totalCountOfDiscounts.toString()}
                                tooltipText={t('Total discount transactions number')}
                                loading={false}
                                error={''}
                                headerFontSize={headerFontSize}
                                valueFontSize={valueFontSize}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DashboardCard
                                headerText={t('Total Discount Amount')}
                                value={getCurrencyWithAmount(
                                    discountValuesTotal.toString(),
                                    currencySymbol,
                                    currencyCode,
                                )}
                                tooltipText={t('Total discount amount')}
                                loading={false}
                                error={''}
                                headerFontSize={headerFontSize}
                                valueFontSize={valueFontSize}
                            />
                        </Grid>
                    </Grid>
                    <Section
                        sx={{
                            marginTop: 2,
                            border: '1px solid #E5E5E5',
                            backgroundColor: '#FAFAFA',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            borderTopLeftRadius: 16,
                            borderTopRightRadius: 16,
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                            cursor: 'default',
                        }}
                    >
                        <Typography variant="h5" sx={{ fontSize: discountTitleSize }}>
                            {t('Discount Type Breakdown')}
                        </Typography>
                    </Section>
                    {discountValues.map((discountValue) => {
                        if (discountValue.title === 'loyaltyGeneralDiscount') {
                            return discountDetailRowComponent(
                                discountValue.title,
                                discountValue.value.toString(),
                                loyaltyGeneralDiscountCount,
                            );
                        }

                        return null;
                    })}
                </Grid>
            </div>
        </SwipeableDrawer>
    );
};

export default DiscountDetailsDrawer;
